import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import Box from 'ui-box';
import {
  FormField,
  majorScale,
  InputGroup,
  Button,
  Card,
  Checkbox,
  Spinner,
  useToast,
} from '../../npaper';
import { IInvitationData, useStores } from '../../stores';
import validateEmail from '../../utils/email-validator';
import { AuthLayout, AuthTitle } from './AuthLayout';

type FormValues = {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  confirmPassword?: string;
  opt_in?: boolean;
  acceptTerms?: boolean;
};

export const AuthInvite = () => {
  // TODO: What if user is not new? (Already existing)
  // TODO: Then we should let him sign in instead: Remove other password and checkboxes and make button "Sign in" and "Forgot your password?".
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  const toaster = useToast();

  const { authStore } = useStores();

  const [networkError, setNetworkError] = useState<string>();

  const invitationKey = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('key')) {
      history.replace('/auth/');
      return '';
    }
    return searchParams.get('key');
  }, [history, location.search]);

  const [invitation, setInvitation] = useState<IInvitationData>();

  useEffect(() => {
    if (!invitationKey) return;
    let isSub = true;
    authStore
      .getInvitation(invitationKey)
      .then((invitation) => isSub && setInvitation(invitation))
      .catch(() => {
        setNetworkError(
          intl.formatMessage({
            defaultMessage:
              'The invitation could not be fetched. Please request a new invitation from the inviter.',
          })
        );
      });
    return () => {
      isSub = false;
    };
  }, [authStore, intl, invitationKey]);

  const {
    register,
    errors,
    handleSubmit,
    watch,
    control,
    setError,
    formState,
  } = useForm<FormValues>();

  const onSubmit = async (values: FormValues) => {
    if (!invitation) return;
    if (invitation.has_user) {
      // Sign in and accept invite
      try {
        await authStore.signIn(values.email, values.password);
        await authStore.acceptInvite(invitation.key);
        history.push('/auth/organisations/');
      } catch (err) {
        if (err.response && err.response.status === 401)
          setError('password', {
            message: intl.formatMessage({
              defaultMessage: 'Email or password wrong',
            }),
          });
        else toaster.showDefaultError(err);
      }
    } else {
      // Create a new user
      try {
        await authStore.signUp(values, invitation.key);
        await authStore.setup();
        toaster.addToast({
          message: intl.formatMessage({
            defaultMessage: 'Thank you, and welcome to Sails!',
          }),
          intent: 'success',
        });
        history.push('/auth/organisations/');
      } catch (err) {
        if (err.response && err.response.data) {
          if (err.response.data['invitation']) {
            toaster.addToast({
              message: intl.formatMessage({
                defaultMessage: 'Invitation has expired',
              }),
              intent: 'danger',
              timeout: 8000,
            });
          } else if (err.response.data['email']) {
            setError('email', {
              message: intl.formatMessage({
                defaultMessage:
                  'A user with that email already exist. Please contact support if you cannot accept the invitation.',
              }),
            });
          } else if (err.response.data['password']) {
            setError('password', {
              message: err.response.data['password'][0],
            });
          }
        } else {
          toaster.showDefaultError(err);
        }
      }
    }
  };

  return (
    <AuthLayout>
      <AuthTitle
        title={
          networkError
            ? intl.formatMessage({
                defaultMessage: 'Invitation not found',
              })
            : invitation && invitation.key_expired
            ? intl.formatMessage({
                defaultMessage: 'Invitation has expired',
              })
            : intl.formatMessage({
                defaultMessage: 'You have an invitation',
              })
        }
        sub={intl.formatMessage({
          defaultMessage: 'Welcome to Sails',
        })}
      />
      {!networkError && !invitation && (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Spinner />
        </Box>
      )}
      {networkError && (
        <Box>
          <p>{networkError}</p>
          <Button
            linkTo="/auth-sign-up"
            minimal
            iconLeft="arrow-left"
            marginTop={majorScale(1)}
          >
            <FormattedMessage id="defaults.back" defaultMessage="Back" />
          </Button>
        </Box>
      )}
      {invitation && invitation.key_expired && (
        <Box>
          <p>
            <FormattedMessage
              defaultMessage="The invitation has expired. Please request a new invitation from <strong>{name}</strong> if you want to join <strong>{organisation}</strong> on Sails."
              values={{
                name: invitation.inviter_name,
                organisation: invitation.organisation_name,
                strong: (chunk: string) => <strong>{chunk}</strong>,
              }}
            />
          </p>
          <Button
            linkTo="/auth-sign-up"
            minimal
            iconLeft="arrow-left"
            marginTop={majorScale(1)}
          >
            <FormattedMessage id="defaults.back" defaultMessage="Back" />
          </Button>
        </Box>
      )}
      {invitation && !invitation.key_expired && (
        <>
          <Box display="flex" alignItems="center">
            {invitation.organisation_thumbnail && (
              <Card
                width={50}
                height={50}
                flexShrink={0}
                marginRight={majorScale(1)}
                border="none"
              >
                <img
                  src={invitation.organisation_thumbnail.small}
                  alt={invitation.organisation_name}
                  width="100%"
                  height="100%"
                  style={{ objectFit: 'contain' }}
                />
              </Card>
            )}
            <p>
              <FormattedMessage
                defaultMessage="{name} invited you to join <strong>{organisation}</strong> on Sails."
                values={{
                  strong: (chunk: string) => <strong>{chunk}</strong>,
                  name: invitation.inviter_name,
                  organisation: invitation.organisation_name,
                }}
              />
            </p>
          </Box>
          <Box is="p" marginY={majorScale(2)}>
            {invitation.has_user ? (
              <FormattedMessage defaultMessage="If you want to accept the invitation, please sign in to your account." />
            ) : (
              <FormattedMessage defaultMessage="If you want to accept the invitation, please create a user." />
            )}
          </Box>

          <FormField
            label={intl.formatMessage({
              defaultMessage: 'Email',
              description: 'Form field label',
            })}
            marginBottom={majorScale(2)}
            className="medium"
          >
            <InputGroup
              name="email"
              ref={register({
                validate: (v: string) =>
                  (!validateEmail(v) &&
                    intl.formatMessage({
                      defaultMessage: 'Please provide a valid email address',
                    })) ||
                  true,
                required: intl.formatMessage({
                  defaultMessage: 'Please enter your email',
                }),
              })}
              defaultValue={invitation.email}
              placeholder={intl.formatMessage({
                defaultMessage: 'Email',
                description: 'Input placeholder',
              })}
              isInvalid={!!errors.email}
              validationMessage={errors.email && errors.email.message}
            />
          </FormField>

          <FormField
            label={intl.formatMessage({
              defaultMessage: 'Password',
              description: 'Form field label',
            })}
            marginBottom={majorScale(2)}
            className="medium"
          >
            <InputGroup
              placeholder={intl.formatMessage({
                defaultMessage: 'Password',
              })}
              type="password"
              name="password"
              ref={register({
                required: intl.formatMessage({
                  defaultMessage: 'Please enter your password',
                  description: 'Password validation required message',
                }),
              })}
              isInvalid={!!errors.password}
              validationMessage={errors.password && errors.password.message}
            />
            {!invitation.has_user && (
              <InputGroup
                placeholder={intl.formatMessage({
                  defaultMessage: 'Repeat password',
                })}
                type="password"
                name="confirmPassword"
                ref={register({
                  required: intl.formatMessage({
                    defaultMessage: 'Please confirm your password',
                    description: 'Password confirm required message',
                  }),
                  validate: (val: string) =>
                    val === watch('password') ||
                    intl.formatMessage({
                      defaultMessage: 'Passwords do not match',
                    }),
                })}
                isInvalid={!!errors.confirmPassword}
                validationMessage={
                  errors.confirmPassword && errors.confirmPassword.message
                }
              />
            )}
          </FormField>

          {!invitation.has_user && (
            <FormField
              label={intl.formatMessage({
                defaultMessage: 'Name',
              })}
            >
              <InputGroup
                placeholder={intl.formatMessage({
                  defaultMessage: 'First name',
                })}
                type="text"
                name="first_name"
                ref={register({
                  required: intl.formatMessage({
                    defaultMessage: 'Please provide your first name',
                  }),
                })}
                isInvalid={!!errors.first_name}
                validationMessage={
                  errors.first_name && errors.first_name.message
                }
              />
              <InputGroup
                placeholder={intl.formatMessage({
                  defaultMessage: 'Last name',
                })}
                type="text"
                name="last_name"
                ref={register({
                  required: intl.formatMessage({
                    defaultMessage: 'Please provide your last name',
                  }),
                })}
                isInvalid={!!errors.last_name}
                validationMessage={errors.last_name && errors.last_name.message}
              />
            </FormField>
          )}

          {!invitation.has_user && (
            <>
              {errors.acceptTerms && (
                <p className="small danger">{errors.acceptTerms.message}</p>
              )}
              <Controller
                control={control}
                name="acceptTerms"
                rules={{
                  required: intl.formatMessage({
                    defaultMessage:
                      'You must agree to our terms to create a user',
                  }),
                }}
                defaultValue={false}
                render={({ onChange, value }) => (
                  <Checkbox
                    small
                    checked={value}
                    onChange={onChange}
                    label={
                      <>
                        Ved at oprette en bruger accepterer du vores{' '}
                        <a
                          className="link small"
                          href="/TODO TODO TODO"
                          target="blank"
                        >
                          Privatlivspolitik
                        </a>{' '}
                        og{' '}
                        <a
                          className="link small"
                          href="/TODO TODO TODO"
                          target="blank"
                        >
                          Forretningsbetingelser
                        </a>
                        .
                      </>
                    }
                    marginBottom={majorScale(2)}
                  />
                )}
              />
              <Controller
                control={control}
                name="opt_in"
                defaultValue={false}
                render={({ onChange, value }) => (
                  <Checkbox
                    small
                    checked={value}
                    onChange={onChange}
                    label="Vil du modtage nye produktinformationer og markedsføringmateriale fra os?"
                    marginBottom={majorScale(2)}
                  />
                )}
              />
            </>
          )}

          <Button
            primary
            width="100%"
            marginBottom={majorScale(2)}
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            {invitation.has_user ? (
              <FormattedMessage defaultMessage="Sign in" />
            ) : (
              <FormattedMessage defaultMessage="Create user" />
            )}
          </Button>
        </>
      )}
    </AuthLayout>
  );
};
